import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';

const PAGE_SIZE = 25;

export function useIncomingRecords(filters = {}) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);

  const {
    source = 'all',
    status = 'all',
    search = '',
    dateFrom = null,
    dateTo = null,
  } = filters;

  const load = useCallback(async () => {
    if (!supabase) return;
    setLoading(true);
    setError('');
    try {
      let query = supabase
        .from('incoming_records')
        .select('*', { count: 'exact' })
        .order('occurred_at', { ascending: false, nullsFirst: false })
        .order('ingested_at', { ascending: false });

      if (source !== 'all') {
        query = query.eq('source', source);
      }
      if (status === 'unmatched') {
        query = query.in('status', ['unmatched', 'suggested']);
      } else if (status !== 'all') {
        query = query.eq('status', status);
      }
      if (search.trim()) {
        const term = search.trim();
        query = query.or(
          `email_hint.ilike.%${term}%,name_hint.ilike.%${term}%,external_id.ilike.%${term}%`
        );
      }
      if (dateFrom) {
        query = query.gte('occurred_at', dateFrom);
      }
      if (dateTo) {
        query = query.lte('occurred_at', dateTo);
      }

      const from = page * PAGE_SIZE;
      const { data, error: qError, count } = await query.range(from, from + PAGE_SIZE - 1);

      if (qError) throw qError;
      setRecords(data || []);
      setTotalCount(count || 0);
    } catch (err) {
      setError(err.message || 'Failed to load incoming records');
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }, [source, status, search, dateFrom, dateTo, page]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setPage(0);
  }, [source, status, search, dateFrom, dateTo]);

  return {
    records,
    loading,
    error,
    totalCount,
    page,
    pageSize: PAGE_SIZE,
    setPage,
    reload: load,
  };
}

export async function fetchUnmatchedCount() {
  if (!supabase) return 0;
  const { count, error } = await supabase
    .from('incoming_records')
    .select('*', { count: 'exact', head: true })
    .in('status', ['unmatched', 'suggested']);
  if (error) return 0;
  return count || 0;
}

export async function fetchSuggestedClients(emailHint) {
  if (!supabase || !emailHint) return [];
  const email = emailHint.toLowerCase();
  const { data } = await supabase
    .from('clients')
    .select('id, login_email, owner_email, business_email, business_name, owner_name, account_state, user_id')
    .or(`login_email.ilike.${email},owner_email.ilike.${email},business_email.ilike.${email}`)
    .is('deleted_at', null)
    .limit(10);
  return data || [];
}
